<template>
  <div class="admin-wrap">
    <div class="admin-header">
      <div class="admin-header-inner">
        <div class="header-left">
          <BackButton v-if="showBack" />
        </div>
        <div class="header-right">
          <div v-if="showSsoSwitch" class="flex items-center gap-3 text-sm">
            <span>Organisation: <strong>{{ tenant }}</strong></span>
            <button class="btn small" type="button" @click="switchOrganisation">Switch</button>
          </div>
          <details v-else class="accounts">
            <summary class="btn ghost small" aria-label="Accounts">
              <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
                <path d="M12 12c2.761 0 5-2.239 5-5s-2.239-5-5-5-5 2.239-5 5 2.239 5 5 5zm0 2c-4.418 0-8 2.239-8 5v1h16v-1c0-2.761-3.582-5-8-5z"/>
              </svg>
            </summary>
            <div class="panel card">
              <div class="row">
                <div class="muted small">Saved accounts</div>
                <div v-if="accounts.length === 0" class="muted small">None saved</div>
                <div v-for="(acc, idx) in accounts" :key="idx" class="account-item">
                  <div class="label">{{ acc.label || acc.tenant || 'Account' }}</div>
                  <div class="meta">{{ acc.tenant }}</div>
                  <div class="actions">
                    <button class="btn small" @click="switchTo(acc)">Switch</button>
                    <button class="btn danger small" @click="removeAccount(idx)">Remove</button>
                  </div>
                </div>
              </div>
              <div class="row actions">
                <button class="btn small" @click="saveCurrent">Save current</button>
                <button class="btn small" @click="gotoLogin">Add new…</button>
              </div>
            </div>
          </details>
          <button class="btn small ghost" @click="logout">Log out</button>
        </div>
      </div>
    </div>
    <section v-if="showOratorProBanner" class="orator-banner" aria-label="Orator Pro announcement">
      <img class="orator-banner-logo" :src="oratorProLogo" alt="Orator Pro">
      <div class="orator-banner-copy">
        <div class="orator-banner-title">Have you heard about Orator Pro?</div>
        <p>Live name pronunciation support for ceremony speakers and event teams.</p>
        <details class="orator-banner-details" @toggle="handleOratorDetailsToggle">
          <summary>Why it helps</summary>
          <div>
            <p>Orator Pro helps graduation teams replace paper name cards with a live ceremony workflow.</p>
            <p>Students can confirm spelling and pronunciation before they reach the stage, and orators see up-to-date guidance as the ceremony changes.</p>
            <p>It is designed for the moments where late arrivals, corrections and live running-order changes need to be handled calmly.</p>
          </div>
        </details>
      </div>
      <a
        class="orator-banner-link"
        href="https://oratorpro.co.uk"
        target="_blank"
        rel="noopener noreferrer"
        @click="trackOratorLink"
      >
        Learn more
      </a>
      <button
        type="button"
        class="orator-banner-dismiss"
        aria-label="Dismiss Orator Pro announcement"
        @click="dismissOratorProBanner"
      >
        &times;
      </button>
    </section>
    <SelfLinkPrompt />
    <router-view @lynda-width="$emit('lynda-width', $event)"></router-view>
  </div>
  
</template>

<script>
import axios from 'axios'
import Auth from '../../api/auth'
import { startAdminAnalyticsSession, trackAdminEvent, toolKeyForRoute } from '../../admin/activity.js'
import { getAccessContext } from '../../admin/access.js'
import { sharedSsoAvailable, startSharedSignIn } from '../../api/sharedSso.js'
import BackButton from './BackButton.vue'
import SelfLinkPrompt from './SelfLinkPrompt.vue'
import OratorProLogo from '../../assets/orator-pro-logo.svg'

const ORATOR_PRO_BANNER_DISMISSED_KEY = 'admin-orator-pro-banner-dismissed'

export default {
  name: "Admin",
  components: { BackButton, SelfLinkPrompt },
  data() {
    return {
      accounts: [],
      tenant: '',
      currentToken: '',
      oratorProBannerDismissed: false,
      oratorProBannerViewed: false,
      oratorProLogo: OratorProLogo,
    }
  },
  created() {
    this.loadAccounts()
    this.currentToken = localStorage.getItem('token') || ''
    this.tenant = this.decodeTenant(this.currentToken)
    this.oratorProBannerDismissed = this.readOratorProBannerDismissed()
    startAdminAnalyticsSession(this.$route)
  },
  mounted() {
    this.trackCurrentToolOpened(this.$route)
    this.trackOratorBannerViewed()
  },
  watch: {
    $route(to) {
      this.currentToken = Auth.getToken()
      this.tenant = this.decodeTenant(this.currentToken)
      startAdminAnalyticsSession(to)
      this.trackCurrentToolOpened(to)
    },
    showOratorProBanner() {
      this.trackOratorBannerViewed()
    }
  },
  computed: {
    showSsoSwitch() {
      return sharedSsoAvailable() && Auth.isSharedSsoSession() && Auth.isTokenValid(this.currentToken)
    },
    showBack() {
      try {
        const name = this.$route && this.$route.name
        const path = (this.$route && this.$route.path) || ''
        // Hide back button on choose screen and bare /admin
        if (name === 'choose' || path === '/admin' || path === '/admin/') return false
        return true
      } catch { return true }
    },
    showOratorProBanner() {
      if (!this.$route || this.$route.path !== '/admin/choose') return false
      if (this.oratorProBannerDismissed) return false
      if (this.tenant === 'gfpevents') return false
      const context = getAccessContext(this.currentToken)
      if (!context.valid || !context.hasUniversities) return false
      const roles = Array.isArray(context.roles) ? context.roles : []
      return roles.length > 0
    }
  },
  methods: {
    switchOrganisation() {
      startSharedSignIn('/admin/choose')
    },
    logout() {
      Auth.logout()
    },
    gotoLogin() {
      this.$router.push({ path: '/admin/login' })
    },
    loadAccounts() {
      try {
        const raw = localStorage.getItem('adminAccounts')
        this.accounts = raw ? JSON.parse(raw) : []
      } catch { this.accounts = [] }
    },
    persistAccounts() {
      localStorage.setItem('adminAccounts', JSON.stringify(this.accounts))
    },
    saveCurrent() {
      const token = localStorage.getItem('token')
      if (!token) return
      const tenant = this.decodeTenant(token)
      const label = window.prompt('Label for this account', tenant || 'Account')
      if (label === null) return
      this.accounts.push({ label, token, tenant })
      this.persistAccounts()
    },
    switchTo(acc) {
      if (!acc || !acc.token) return
      localStorage.setItem('token', acc.token)
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + acc.token
      this.currentToken = acc.token
      this.tenant = acc.tenant || this.decodeTenant(acc.token)
      // Soft reload into admin root
      this.$router.push({ path: '/admin/choose' })
    },
    removeAccount(idx) {
      this.accounts.splice(idx, 1)
      this.persistAccounts()
    },
    decodeTenant(raw) {
      try {
        if (!raw) return ''
        const part = raw.split('.')[1]
        if (!part) return ''
        const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
        const payload = JSON.parse(decodeURIComponent(escape(json)))
        return payload.tenant || ''
      } catch { return '' }
    },
    readOratorProBannerDismissed() {
      try {
        return localStorage.getItem(ORATOR_PRO_BANNER_DISMISSED_KEY) === 'true'
      } catch { return false }
    },
    dismissOratorProBanner() {
      trackAdminEvent('orator_banner_dismissed', {
        toolKey: 'orator_pro',
      })
      this.oratorProBannerDismissed = true
      try {
        localStorage.setItem(ORATOR_PRO_BANNER_DISMISSED_KEY, 'true')
      } catch { /* no-op */ }
    },
    trackOratorBannerViewed() {
      if (!this.showOratorProBanner || this.oratorProBannerViewed) return
      this.oratorProBannerViewed = true
      trackAdminEvent('orator_banner_viewed', {
        toolKey: 'orator_pro',
      })
    },
    handleOratorDetailsToggle(event) {
      if (!event || !event.target || !event.target.open) return
      trackAdminEvent('orator_banner_expanded', {
        toolKey: 'orator_pro',
      })
    },
    trackOratorLink() {
      trackAdminEvent('orator_banner_clicked', {
        toolKey: 'orator_pro',
      })
    },
    trackCurrentToolOpened(route) {
      const toolKey = toolKeyForRoute(route)
      trackAdminEvent('admin_tool_opened', {
        route,
        toolKey,
        metadata: {
          routeName: route && route.name,
          path: route && route.path,
        },
      })
    }
  }
};
</script>

<style scoped>
.admin-wrap { position: relative; }
.admin-header { position: relative; border-bottom: 1px solid #f3f4f6; margin-bottom: 12px; }
.admin-header-inner { max-width: 1100px; margin: 0 auto; padding: 12px 16px; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.orator-banner { max-width: 768px; margin: 0 auto 18px; padding: 14px 16px; border: 1px solid #d8e6e9; border-radius: 8px; background: #fff; color: #1f2937; display: grid; grid-template-columns: 118px 1fr auto auto; align-items: center; gap: 14px; box-shadow: 0 1px 2px rgba(17, 24, 39, 0.05); }
.orator-banner-logo { width: 112px; max-height: 24px; object-fit: contain; }
.orator-banner-copy { min-width: 0; display: grid; gap: 2px; }
.orator-banner-title { font-weight: 700; color: #111827; }
.orator-banner-copy p { margin: 0; font-size: 14px; color: #374151; }
.orator-banner-details { margin-top: 5px; font-size: 13px; color: #4b5563; }
.orator-banner-details summary { width: max-content; color: #08697b; cursor: pointer; font-weight: 700; list-style-position: outside; }
.orator-banner-details summary:hover { color: #064f5d; }
.orator-banner-details div { margin-top: 8px; display: grid; gap: 5px; }
.orator-banner-details p { font-size: 13px; line-height: 1.45; color: #4b5563; }
.orator-banner-link { padding: 7px 10px; border: 1px solid #b8d9df; border-radius: 6px; background: #f3fbfc; color: #08697b; font-size: 13px; font-weight: 700; text-decoration: none; white-space: nowrap; }
.orator-banner-link:hover { background: #e7f5f7; }
.orator-banner-dismiss { width: 30px; height: 30px; border: 1px solid #d1d5db; border-radius: 6px; background: #fff; color: #6b7280; cursor: pointer; font-size: 20px; line-height: 1; display: inline-flex; align-items: center; justify-content: center; }
.orator-banner-dismiss:hover { background: #f9fafb; color: #374151; }
.header-left, .header-right { display: inline-flex; align-items: center; gap: 8px; }
.btn { padding: 6px 10px; border: 1px solid #e5e7eb; background: #fff; border-radius: 6px; cursor: pointer; font-size: 0.9rem; }
.btn.small { padding: 4px 8px; font-size: 12px; }
.btn.ghost { background: #fff; border-color: #e5e7eb; }
.btn.danger { border-color: #fecaca; background: #fee2e2; color: #991b1b; }
.accounts { position: relative; }
.accounts > summary { list-style: none; }
.accounts > summary::marker, .accounts > summary::-webkit-details-marker { display: none; }
.accounts > summary .icon { display: inline-block; vertical-align: middle; color: #374151; }
.panel.card { position: absolute; right: 0; top: 100%; min-width: 300px; padding: 12px; background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 6px 20px rgba(0,0,0,.08); }
.row { display: grid; gap: 8px; }
.row.actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 6px; }
.account-item { display: grid; grid-template-columns: 1fr auto; gap: 4px 8px; align-items: center; padding: 6px 0; border-bottom: 1px solid #f3f4f6; }
.account-item:last-child { border-bottom: 0; }
.account-item .label { font-weight: 600; color: #111827; }
.account-item .meta { font-size: 12px; color: #6b7280; }
.account-item .actions { display: flex; gap: 6px; }
.small { font-size: 12px; }
.muted { color: #6b7280; }
@media (max-width: 640px) {
  .admin-header-inner { position: relative; flex-wrap: wrap; }
  .header-right { flex-wrap: wrap; margin-left: auto; }
  .accounts { position: static; }
  .panel.card { z-index: 50; min-width: 0; width: min(300px, 100%); }
  .account-item { grid-template-columns: minmax(0, 1fr) auto; overflow-wrap: anywhere; }
  .admin-header .btn { min-height: 44px; }
  /* Safari zooms focused fields with text smaller than 16px. */
  .admin-wrap ::v-deep input:not([type='checkbox']):not([type='radio']):not([type='range']):not([type='file']):not([type='color']),
  .admin-wrap ::v-deep select,
  .admin-wrap ::v-deep textarea { font-size: 16px; max-width: 100%; min-width: 0; }
  .orator-banner { margin-left: 8px; margin-right: 8px; grid-template-columns: 1fr auto; align-items: flex-start; }
  .orator-banner-logo { grid-column: 1 / 2; width: 118px; margin-top: 3px; }
  .orator-banner-copy { grid-column: 1 / 3; }
  .orator-banner-link { grid-column: 1 / 2; justify-self: start; }
  .orator-banner-dismiss { grid-column: 2 / 3; grid-row: 1 / 2; }
}
</style>
